import { logger } from "@/modules/logger";
import type { AppStore, PublicAppStore } from "@/store";
import { GatedFeature } from "@/store/feature-gates/types";
import { PaywallContext } from "@/domains/metrics/tracked-event";
import { getContactDisplayNames, getNoteMetadata } from "@/store/note/metadata";
import { INoteObservable, NoteContentVersion, NoteSuggestionData } from "@/store/note/types";
import {
  ChangeTableOperation,
  ChangeTablePosition,
  ChangeTableTarget,
  HtmlPlaceholderPreset,
  MemCommonCloseMentionCondition,
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  MemCommonEditorClickedItemKind,
  MemCommonEditorContext,
  MemCommonEditorEvent,
  MemCommonEditorEventKind,
  MemCommonEditorOperationLogOperation,
  MemCommonEditorOperationLogSeverity,
  MemCommonEditorFileRejectionErrorCode,
  MemCommonEditorImageRejectionErrorCode,
  MemCommonEditorInitializer,
  MemCommonEditorTheme,
  MemCommonMentionKeyDownKind,
  MemCommonMentionKind,
  MemCommonTableMenuKind,
  MemCommonEditorContentUnderstandingInfo,
  PatchContentMode,
  ValidateFileBeforeInsertResult,
  editorContextModule,
} from "@mem-labs/common-editor";
import { fromUint8Array, toUint8Array } from "js-base64";
import { debounce, filter, isEqual } from "lodash-es";
import {
  IReactionDisposer,
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";
import * as Y from "yjs";
import { DateTime } from "luxon";
import { IContactModel } from "@/store/contacts/types";
import {
  MDSFloatingActionMenuDropdownProps,
  MDSIconKind,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
  MDSShortcutKey,
  MDSShortcutKeyCharacter,
} from "@/mds";
import { objectModule } from "@/modules/object";
import { hasMetaGeneratorTag } from "@/modules/html";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { windowModule } from "@/modules/window";
import { clientEnvModule } from "@/modules/client-env";
import { UseFloatingOptions } from "@floating-ui/react";
import { fileModule } from "@/modules/file";
import { fileModule as commonEditorFileModule } from "@mem-labs/common-editor";
import { toastModule } from "@/modules/toast";
import { EventContext } from "@/domains/metrics/context";
import { MentionTooltipParams, NoteEditorMode, NoteType } from "@/components/note/types";
import { downloadImageFromUrl } from "@/components/note/media-viewer/media-download";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { getQueuedDocumentUpdatesForOperations } from "@/store/sync/operations/helpers/notes/getQueuedDocumentUpdates";
import { Optional } from "@/domains/common/types";
import { NoteObservable } from "@/store/note/NoteObservable";
import {
  extractFileIdFromAttachmentUrl,
  mapContentUnderstandingState,
} from "@/store/note-attachments/mapContentUnderstandingState";
import { assetProxyModule } from "@mem-labs/common-editor";
import { MDSTheme } from "@/mds";
import { ThemeType } from "@/mds/themes/theme-context";
import { Editor } from "@tiptap/core";
import { countPdfPages } from "@/modules/file/pdf";
import {
  MemCommonEditorMentionChip,
  MemCommonEditorMentions,
} from "@/components/note/store/mem-common-editor-mentions";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

type ImageLightboxItem = {
  src: string;
  alt?: string;
};

type ClickedImagePayloadItem = {
  imageId: string | null;
  src: string;
  alt?: string;
};

const mapPayloadImagesToImageLightboxItems = (
  images?: ClickedImagePayloadItem[]
): ImageLightboxItem[] => {
  if (!images?.length) {
    return [];
  }

  return images.reduce<ImageLightboxItem[]>((accumulator, image) => {
    const src = image.src;
    if (!src) {
      return accumulator;
    }

    const alt = image.alt || undefined;
    accumulator.push(
      alt
        ? {
            src,
            alt,
          }
        : {
            src,
          }
    );

    return accumulator;
  }, []);
};

const ATTACHMENT_INACCESSIBLE_TOAST_MESSAGE =
  "Attachment inaccessible. Try copy/paste or download/upload instead";

export class MemCommonEditorStore {
  private autoFocus = false;
  private enableImageZoomCursor = false;
  private enableImageHoverToolbar = false;
  autoFocusPosition?: "start" | "end";
  private disableGlobalListeners = false;
  private goToMention?: INotesViewPageStore["goToMention"];
  private openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
  private openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
  private openPdfPageLimitWarningModal: (args: { pageCount: number }) => Promise<boolean>;
  private placeholder = "";
  private htmlPlaceholderPreset?: HtmlPlaceholderPreset;
  private processedUpdateIds = new Set<string>();
  private publicStore: PublicAppStore;
  private store?: AppStore;
  private reactionDisposers: IReactionDisposer[] = [];
  private reactionDisposersRegistered = false;
  private onReadOnlyNoteClicked?: () => void;
  private onImageClicked?: (imageSrc: string | null, allImages: ImageLightboxItem[]) => void;
  private onPdfPreviewClicked?: (downloadUrl: string, fileName: string) => void;
  private openRenameFileModal?: INotesViewPageStore["openRenameFileModal"];
  private readonly mode: NoteEditorMode = NoteEditorMode.Editable;

  readonly id: string;
  readonly dispatchAction: (event: MemCommonEditorAction) => void;
  readonly findInNote?: INotesViewPageStore["findInNote"];
  editor?: Editor;
  theme: MDSTheme;
  themeType: ThemeType;
  noteType: NoteType;
  isShowingDifferences = false;
  isNoteEmpty = false;
  edited = false;
  mentionQuery = "";
  mentionClientRect: DOMRect | undefined = undefined;
  mentionAvailableChips: MemCommonEditorMentionChip[] | undefined = undefined;
  scrollIntoViewMentionKeyboardSelectionChipIndex = false;
  mentionKeyboardSelectionChipIndex = 1;
  mouseY = 0;
  lockedMouseY = 0;
  tableMenuClientRect: DOMRect | undefined = undefined;
  tableMenuPlacement: UseFloatingOptions["placement"] = "top-start";
  tableMenuOffsetOptions?: MDSFloatingActionMenuDropdownProps["offsetOptions"];
  tableMenuContentList: MDSMenuContentList = [];
  fileMenuClientRect: DOMRect | undefined = undefined;
  fileMenuContentList: MDSMenuContentList = [];
  pasteMenuClientRect: DOMRect | undefined = undefined;
  pasteMenuContentList: MDSMenuContentList = [];
  pasteMenuKeyboardSelectionIndex = 0;
  noteObservable?: INoteObservable | TemplateObservable;
  myAccount?: IContactModel;
  /** @description Force a common-editor / Tiptap re-rendering to remove discarded edits. */
  getAvailableChipsCalls = 0;
  mentionTooltipParams: MentionTooltipParams = {
    top: 0,
    left: 0,
    visible: false,
    itemId: "",
  };
  onGhostTextAccepted: (() => void) | undefined = undefined;

  // Do not clear on reload.
  reloadRequired = false;
  hasContentError = false;
  hasFatalError = false;
  editorContentRendered = false;
  showDifferencesAction: MemCommonEditorAction | undefined = undefined;
  versionToAccept: Optional<NoteContentVersion, "model_version"> | undefined = undefined;
  baseVersion: Optional<NoteContentVersion, "model_version"> | undefined = undefined;
  mentions?: MemCommonEditorMentions;

  constructor({
    autoFocus,
    autoFocusPosition,
    disableGlobalListeners = false,
    dispatchAction,
    goToMention,
    openFileUploadRejectedModal,
    openImageUploadRejectedModal,
    openPdfPageLimitWarningModal,
    findInNote,
    noteObservable,
    myAccount,
    placeholder = "Add to your Mem",
    htmlPlaceholderPreset,
    publicStore,
    appStore,
    onReadOnlyNoteClicked,
    onImageClicked,
    onPdfPreviewClicked,
    openRenameFileModal,
    enableImageZoomCursor = false,
    enableImageHoverToolbar = false,
    mode,
    theme,
    themeType,
    noteType = NoteType.Note,
  }: {
    autoFocus: boolean;
    autoFocusPosition?: "start" | "end";
    disableGlobalListeners?: boolean;
    goToMention?: INotesViewPageStore["goToMention"];
    openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
    openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
    openPdfPageLimitWarningModal: (args: { pageCount: number }) => Promise<boolean>;
    findInNote?: INotesViewPageStore["findInNote"];
    dispatchAction: (event: MemCommonEditorAction) => void;
    noteObservable: INoteObservable | TemplateObservable;
    myAccount?: IContactModel;
    placeholder?: string;
    htmlPlaceholderPreset?: HtmlPlaceholderPreset;
    publicStore: PublicAppStore;
    appStore?: AppStore;
    onReadOnlyNoteClicked?: () => void;
    onImageClicked?: (imageSrc: string | null, allImages: ImageLightboxItem[]) => void;
    onPdfPreviewClicked?: (downloadUrl: string, fileName: string) => void;
    openRenameFileModal?: INotesViewPageStore["openRenameFileModal"];
    enableImageZoomCursor?: boolean;
    enableImageHoverToolbar?: boolean;
    mode: NoteEditorMode;
    theme: MDSTheme;
    themeType: ThemeType;
    noteType?: NoteType;
  }) {
    this.autoFocus = !!autoFocus;
    this.autoFocusPosition = autoFocusPosition;
    this.disableGlobalListeners = !!disableGlobalListeners;
    this.dispatchAction = dispatchAction;
    this.goToMention = goToMention;
    this.openFileUploadRejectedModal = openFileUploadRejectedModal;
    this.openImageUploadRejectedModal = openImageUploadRejectedModal;
    this.openPdfPageLimitWarningModal = openPdfPageLimitWarningModal;
    this.findInNote = findInNote;
    this.id = noteObservable.id;
    this.placeholder = placeholder;
    this.htmlPlaceholderPreset = htmlPlaceholderPreset;
    this.publicStore = publicStore;
    this.store = appStore;
    this.onReadOnlyNoteClicked = onReadOnlyNoteClicked;
    this.onImageClicked = onImageClicked;
    this.onPdfPreviewClicked = onPdfPreviewClicked;
    this.openRenameFileModal = openRenameFileModal;
    this.enableImageZoomCursor = !!enableImageZoomCursor;
    this.enableImageHoverToolbar = !!enableImageHoverToolbar;
    this.mode = mode;
    this.theme = theme;
    this.themeType = themeType;
    this.noteType = noteType;

    if (appStore) {
      this.mentions = new MemCommonEditorMentions({
        store: appStore,
        dispatchAction: dispatchAction,
      });
    }

    logger.debug({
      message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Open ${this.noteType} editor (${this.mode})`,
    });

    this.noteObservable = noteObservable;
    this.myAccount = myAccount;

    makeObservable<
      this,
      | "autoFocus"
      | "enableImageZoomCursor"
      | "enableImageHoverToolbar"
      | "autoFocusPosition"
      | "disableGlobalListeners"
      | "dispatchAction"
      | "goToMention"
      | "openFileUploadRejectedModal"
      | "openImageUploadRejectedModal"
      | "openPdfPageLimitWarningModal"
      | "findInNote"
      | "id"
      | "placeholder"
      | "htmlPlaceholderPreset"
      | "processedUpdateIds"
      | "publicStore"
      | "appStore"
      | "reactionDisposers"
      | "reactionDisposersRegistered"
      | "readOnly"
      | "versionToAccept"
      | "baseVersion"
      | "onReadOnlyNoteClicked"
      | "onImageClicked"
      | "onPdfPreviewClicked"
      | "openRenameFileModal"
      | "setOnImageClicked"
      | "setImageViewerEnabled"
      | "openImageViewerForSrc"
      | "setMentionTooltipParams"
      | "mode"
      | "uiTheme"
      | "noteType"
      | "debouncedReloadRequired"
      | "clearSuggestionPreviewAndReload"
      | "isShowingSuggestionDifferences"
      | "preparePasteClipboardContentAction"
      | "acceptCleanupAfterPaste"
      | "closePasteMenu"
      | "handlePasteKeydown"
      | "store"
      | "buildValidateFileBeforeInsert"
    >(this, {
      mentions: true,
      processSuggestionData: false,
      debouncedReloadRequired: false,
      clearSuggestionPreviewAndReload: false,
      appStore: false,
      preparePasteClipboardContentAction: false,
      isInsertMenuActive: false,
      memoryQueue: false,
      autoFocus: false,
      enableImageZoomCursor: false,
      enableImageHoverToolbar: false,
      autoFocusPosition: observable,
      disableGlobalListeners: false,
      dispatchAction: false,
      goToMention: false,
      openFileUploadRejectedModal: false,
      openImageUploadRejectedModal: false,
      openPdfPageLimitWarningModal: false,
      findInNote: false,
      id: false,
      placeholder: false,
      htmlPlaceholderPreset: false,
      processedUpdateIds: false,
      publicStore: false,
      reactionDisposers: false,
      reactionDisposersRegistered: false,
      readOnly: false,
      onReadOnlyNoteClicked: false,
      onImageClicked: false,
      onPdfPreviewClicked: false,
      openRenameFileModal: false,
      setOnImageClicked: false,
      setImageViewerEnabled: action,
      openImageViewerForSrc: false,
      mode: false,
      noteType: false,
      baseVersion: false,

      uiTheme: computed,
      editor: observable,
      theme: observable,
      themeType: observable,
      edited: observable,
      isNoteEmpty: observable,
      isShowingDifferences: observable,
      isShowingSuggestionDifferences: computed,
      mentionQuery: observable,
      mentionClientRect: observable,
      mentionAvailableChips: observable,
      mentionKeyboardSelectionChipIndex: observable,
      showDifferencesToAcceptVoiceTranscriptChanges: action,
      showDifferencesAction: observable,
      scrollIntoViewMentionKeyboardSelectionChipIndex: observable,
      mouseY: observable,
      lockedMouseY: observable,
      tableMenuContentList: observable,
      tableMenuClientRect: observable,
      tableMenuPlacement: observable,
      tableMenuOffsetOptions: observable,
      fileMenuClientRect: observable,
      fileMenuContentList: observable,
      pasteMenuClientRect: observable,
      pasteMenuContentList: observable,
      pasteMenuKeyboardSelectionIndex: observable,
      noteObservable: observable,
      myAccount: observable,
      reloadRequired: observable,
      hasContentError: observable,
      hasFatalError: observable,
      editorContentRendered: observable,
      versionToAccept: observable,

      registerReactions: false,
      mount: false,
      unmount: false,
      getAvailableChipsCalls: false,
      store: false,
      buildValidateFileBeforeInsert: false,

      setTheme: action,
      setMentionQuery: action,
      setMentionClientRect: action,
      setMentionAvailableChips: action,
      setMentionKeyboardSelectedChipIndex: action,
      setMouseY: action,
      setTableMenuClientRect: action,
      setTableMenuPlacement: action,
      setTableMenuOffsetOptions: action,
      setTableMenuContentList: action,
      setFileMenuClientRect: action,
      setFileMenuContentList: action,
      resetFileMenu: action,
      setPasteMenuClientRect: action,
      setPasteMenuContentList: action,
      setPasteMenuKeyboardSelectedIndex: action,
      closePasteMenu: action,
      handleSelectPasteMenuItem: action,
      setNote: action,
      setReloadRequired: action,
      setHasContentError: action,
      setHasFatalError: action,
      handleDropdownHover: action,
      handleSelectMention: action,
      showDifferencesBetweenVersions: action,
      closeSuggestion: action,
      closeDifferences: action,
      queuedUpdates: computed,
      editorInitializer: computed,
      editorEventHandler: computed,
      mentionContentList: computed,
      pasteMenuContentListWithSelection: computed,
      hasSources: computed,
      authorNames: computed,
      metadata: computed,
      metadataTooltip: computed,
      isTableMenuOpen: computed,
      mentionTooltipParams: observable,
      setMentionTooltipParams: action,
      resetMentionTooltipParams: action,
      acceptCleanupAfterPaste: action,
      handlePasteKeydown: action,
      onGhostTextAccepted: observable,
      setOnGhostTextAccepted: action,
      showGhostText: action,
      clearGhostText: action,
    });
  }

  private get readOnly() {
    return this.mode !== NoteEditorMode.Editable;
  }

  registerReactions() {
    if (this.reactionDisposersRegistered) {
      return;
    }

    this.reactionDisposers.push(
      reaction(
        () => this.reloadRequired,
        reload => {
          if (!reload) return;

          setTimeout(() => {
            // After unmounting editor.
            this.setReloadRequired(false);
          });
        }
      ),
      reaction(
        () => ({
          isAwaitingAudioRecordingTransformation:
            !!this.noteObservable?.isAwaitingAudioRecordingTransformation,
          canWrite: this.noteObservable?.canWrite,
          isTrashed: this.noteObservable?.isTrashed,
        }),
        ({ isAwaitingAudioRecordingTransformation, isTrashed, canWrite }) => {
          if (this.readOnly) return;

          if (isAwaitingAudioRecordingTransformation) {
            this.dispatchAction({
              kind: MemCommonEditorActionKind.SetReadOnly,
              payload: { readOnly: true },
            });
            return;
          }

          const readOnly = (() => {
            if (isTrashed) return true;

            if (typeof canWrite === "boolean") return !canWrite;
          })();

          if (typeof readOnly !== "boolean") return;

          this.dispatchAction({
            kind: MemCommonEditorActionKind.SetReadOnly,
            payload: { readOnly },
          });
        }
      ),
      reaction(
        () => this.noteObservable?.remoteContent,
        encodedContentUpdate => {
          if (!encodedContentUpdate) return;

          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Remote update received, ${encodedContentUpdate.length} bytes`,
          });
          this.dispatchAction({
            kind: MemCommonEditorActionKind.ApplyRemoteUpdate,
            payload: {
              encodedContentUpdate,
            },
          });
        }
      ),
      reaction(
        () => (this.isShowingDifferences ? undefined : this.queuedUpdates),
        encodedContentUpdates => {
          if (!encodedContentUpdates) return;

          const filteredUpdates = filter(
            encodedContentUpdates,
            e => !this.processedUpdateIds.has(e.id) && e.encodedContentDiff.length > 0
          );

          if (filteredUpdates?.[0]?.isDestructive) {
            logger.error({
              message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Destructive update, reloading...`,
              info: {
                operation: encodedContentUpdates[0],
              },
            });
            this.debouncedReloadRequired();
            return;
          }

          const filteredUpdateIds = filteredUpdates.map(e => e.id);
          const updates = filteredUpdates.map(e => toUint8Array(e.encodedContentDiff));

          if (updates.length === 0) return;

          try {
            const update = Y.mergeUpdates(updates);
            const encodedContentUpdate = fromUint8Array(update);

            logger.debug({
              message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Local updates, ${encodedContentUpdate.length} bytes`,
              info: {
                encodedContentUpdateIds: encodedContentUpdates.map(e => e.id),
                processedUpdateIds: [...this.processedUpdateIds],
                filteredUpdateIds,
              },
            });

            this.dispatchAction({
              kind: MemCommonEditorActionKind.ApplyRemoteUpdate,
              payload: { encodedContentUpdate },
            });

            runInAction(() => {
              this.processedUpdateIds = new Set([...this.processedUpdateIds, ...filteredUpdateIds]);
            });
          } catch (error) {
            logger.error({
              message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Failed to apply remote update`,
              info: {
                error: objectModule.safeErrorAsJson(error as Error),
                filteredUpdates,
              },
            });
          }
        }
      ),
      reaction(
        () => {
          return {
            condition: this.mentionContentList.length
              ? MemCommonCloseMentionCondition.RenderingResults
              : MemCommonCloseMentionCondition.NoResults,
            send: this.mentionQuery.length > 0,
          };
        },
        ({ condition, send }) => {
          if (!send) return;

          this.dispatchAction({
            kind: MemCommonEditorActionKind.CloseMention,
            payload: { condition },
          });
        }
      ),
      reaction(
        () => this.noteObservable?.suggestionData,
        (suggestionData, prevSuggestionData) => {
          if (suggestionData?.id && suggestionData.id === prevSuggestionData?.id) {
            logger.debug({
              message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Suggestion id unchanged, skipping reload...`,
            });
            return;
          }

          if (!suggestionData) {
            logger.debug({
              message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) No suggestion data received, reloading...`,
              info: {
                isShowingDifferences: this.isShowingDifferences,
                isShowingSuggestionDifferences: this.isShowingSuggestionDifferences,
              },
            });
            if (this.isShowingSuggestionDifferences) {
              this.clearSuggestionPreviewAndReload({
                reason: "suggestion data was removed",
              });
            }
            return;
          }

          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Suggestion data received, reloading to enter diff mode...`,
            info: suggestionData,
          });
          this.processSuggestionData({ suggestionData, isUpdate: true });
        }
      ),
      reaction(
        () => ({
          searchQuery: this.findInNote?.searchQuery ?? "",
          currentMatchIndex: this.findInNote?.currentMatchIndex ?? 0,
        }),
        ({ searchQuery, currentMatchIndex }) => {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Updated search query...`,
            info: {
              searchQuery,
              currentMatchIndex,
            },
          });
          this.dispatchAction({
            kind: MemCommonEditorActionKind.SetSearch,
            payload: { searchQuery, currentMatchIndex },
          });
        }
      ),
      reaction(
        () => !!this.findInNote?.isOpen,
        (findInNoteIsOpen, prevFindInNoteIsOpen) => {
          if (findInNoteIsOpen || !prevFindInNoteIsOpen) return;

          this.dispatchAction({
            kind: MemCommonEditorActionKind.FocusEditor,
            payload: null,
          });
        }
      ),
      reaction(
        () => {
          if (!this.store) return undefined;
          const noteAttachmentsObs = this.store.spaceAccountNoteAttachments.getAttachmentsForNote(
            this.id
          );

          const includeDisplayState =
            this.store.spaceAccountFeatureFlags.getFlagStatus("content-understanding") ?? false;

          const cuMapping: Record<string, MemCommonEditorContentUnderstandingInfo | undefined> = {};
          for (const attachment of noteAttachmentsObs.attachments) {
            if (!attachment.attachmentUrl) continue;
            const cuInfo = mapContentUnderstandingState({
              extractionStatus: attachment.extractionStatus,
              indexingStatus: attachment.indexingStatus,
              processingInfo: attachment.processingInfo,
              mediaKind: attachment.mimeType?.startsWith("image/") ? "image" : "file",
              suggestedSearch: attachment.suggestedSearch,
              ocrMarkdown: attachment.ocrMarkdown,
              includeDisplayState,
            });
            if (!cuInfo) continue;

            // Primary key: UUID from the attachment URL (upload-time convention).
            const fileId = extractFileIdFromAttachmentUrl(attachment.attachmentUrl);
            if (fileId) cuMapping[fileId] = cuInfo;

            // Secondary key: unwrapped attachment URL. Enables consumer fallback
            // when a node's imageId/fileId is null (old notes, alternate insertion
            // paths). URL equality is the only identity stable across producer
            // (SpaceAccountNoteAttachment.attachment_url) and consumer (TipTap
            // node.attrs.src / downloadUrl).
            const unwrappedUrl =
              assetProxyModule.extractOriginalUrlFromProxy({
                proxiedUrl: attachment.attachmentUrl,
              }) ?? attachment.attachmentUrl;
            cuMapping[unwrappedUrl] = cuInfo;
          }
          return cuMapping;
        },
        cuMapping => {
          if (cuMapping && Object.keys(cuMapping).length > 0) {
            this.dispatchAction({
              kind: MemCommonEditorActionKind.UpdateFileContentUnderstanding,
              payload: { files: cuMapping },
            });
          }
        },
        { fireImmediately: false, equals: comparer.structural }
      )
    );
  }

  mount(): void {
    /**
     * The `MemCommonEditorStore` is tightly coupled to our ui - we need to tie
     * some of its lifecycle methods to our component lifecycle.
     *
     * This will ensure the contents of the editor remain up-to-date (E.g. when
     * receiving remote updates).
     *
     * If `mode` is set to "static", the editor will not have any lifecycle
     * methods registered.
     */

    if (this.mode === NoteEditorMode.Static) {
      return;
    }

    this.registerReactions();
    this.publicStore.interface.addReloadRequiredListener(this.id, this.setReloadRequired);
  }

  async unmount(): Promise<void> {
    try {
      if (this.mode === NoteEditorMode.Static) {
        return;
      }

      this.publicStore.interface.removeReloadRequiredListener(this.id, this.setReloadRequired);

      logger.debug({
        message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Shutting down ${this.readOnly ? "read-only" : "writable"} note editor`,
      });

      this.dispatchAction({
        kind: MemCommonEditorActionKind.Shutdown,
        payload: null,
      });

      // Delay dispose so queued updates are sent and processed.
      logger.debug({
        message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Disposing ${this.reactionDisposers.length} reactions`,
      });

      this.reactionDisposers.forEach(dispose => dispose());

      await this.memoryQueue?.waitForQueue();

      if (this.isShowingDifferences) return;

      if (!(this.noteObservable instanceof TemplateObservable)) {
        if (!this.readOnly && this.isNoteEmpty) {
          // If the user reopens and enters something, we shouldn't do this.
          await this.noteObservable?.deleteEmptyNote();
        }
      }
    } finally {
      this.setNote(undefined);
    }
  }

  get memoryQueue() {
    return this.noteObservable?.memoryQueue;
  }

  get queuedUpdates() {
    const queuedUpdates = this.noteObservable?.queuedDocumentUpdates ?? [];
    const memoryUpdates = getQueuedDocumentUpdatesForOperations({
      operations: this.memoryQueue?.operations.map(e => e.operation) ?? [],
      id: this.id,
    });

    if (memoryUpdates[0]?.isDestructive) return memoryUpdates;

    return [...queuedUpdates, ...memoryUpdates];
  }

  get uiTheme() {
    return this.themeType === ThemeType.HarvestMoon
      ? MemCommonEditorTheme.HarvestMoon
      : MemCommonEditorTheme.Harvest;
  }

  // ACTIONS
  setTheme({ theme, themeType }: { theme: MDSTheme; themeType: ThemeType }) {
    this.themeType = themeType;
    this.theme = theme;

    this.dispatchAction({
      kind: MemCommonEditorActionKind.SetTheme,
      payload: { theme: this.uiTheme },
    });
  }

  setOnImageClicked(
    onImageClicked?: (imageSrc: string | null, allImages: ImageLightboxItem[]) => void
  ) {
    this.onImageClicked = onImageClicked;
  }

  setImageViewerEnabled(enabled: boolean) {
    const hasChanged =
      this.enableImageZoomCursor !== enabled || this.enableImageHoverToolbar !== enabled;

    this.enableImageZoomCursor = enabled;
    this.enableImageHoverToolbar = enabled;

    if (!hasChanged) {
      return;
    }

    type CommonEditorConfigStorageShape = {
      commonEditorConfig?: {
        enableImageZoomCursor?: boolean;
        enableImageHoverToolbar?: boolean;
      };
    };

    // Keep the live editor config in sync with dynamic feature-flag updates.
    // This avoids requiring a full editor/store remount when the flag hydrates.
    const commonEditorConfig = (this.editor?.storage as CommonEditorConfigStorageShape | undefined)
      ?.commonEditorConfig;
    if (!commonEditorConfig || !this.editor?.view || !this.editor?.state?.tr) {
      return;
    }

    commonEditorConfig.enableImageZoomCursor = enabled;
    commonEditorConfig.enableImageHoverToolbar = enabled;

    const transaction = this.editor.state.tr.setMeta("mem-common-editor-config-updated", true);
    this.editor.view.dispatch(transaction);
  }

  openImageViewerForSrc(imageSrc: string | null, allImages?: ImageLightboxItem[]) {
    if (!imageSrc) return;

    const resolvedImages = allImages?.length ? allImages : [{ src: imageSrc }];
    this.onImageClicked?.(imageSrc, resolvedImages);
  }

  setMentionQuery(mentionQuery: string) {
    this.mentionQuery = mentionQuery;
  }

  setMentionClientRect(mentionClientRect?: DOMRect) {
    this.mentionClientRect = mentionClientRect;
  }

  setMentionAvailableChips(mentionAvailableChips?: MemCommonEditorMentionChip[]) {
    this.mentionAvailableChips = mentionAvailableChips;
    this.setMentionKeyboardSelectedChipIndex(mentionAvailableChips?.length ? 0 : -1);
  }

  setMentionKeyboardSelectedChipIndex(mentionKeyboardSelectionChipIndex: number) {
    // Lock hover to select until mouse is moved.
    this.lockedMouseY = this.mouseY;
    this.mentionKeyboardSelectionChipIndex = mentionKeyboardSelectionChipIndex;
    this.scrollIntoViewMentionKeyboardSelectionChipIndex = true;
  }

  setMouseY(mouseY: number) {
    this.mouseY = mouseY;
    if (Math.abs(this.lockedMouseY - mouseY) > 2) {
      // Mouse was moved, unlock hover to select.
      this.lockedMouseY = 0;
    }
  }

  setTableMenuClientRect(clientRect?: DOMRect) {
    this.tableMenuClientRect = clientRect;
  }

  setTableMenuPlacement(placement: UseFloatingOptions["placement"]) {
    this.tableMenuPlacement = placement;
  }

  setTableMenuOffsetOptions(offsetOptions?: MDSFloatingActionMenuDropdownProps["offsetOptions"]) {
    this.tableMenuOffsetOptions = offsetOptions;
  }

  setTableMenuContentList(contentList: MDSMenuContentList) {
    this.tableMenuContentList = contentList;
  }

  setFileMenuClientRect(clientRect?: DOMRect) {
    this.fileMenuClientRect = clientRect;
  }

  setFileMenuContentList(contentList: MDSMenuContentList) {
    this.fileMenuContentList = contentList;
  }

  resetFileMenu() {
    this.fileMenuClientRect = undefined;
    this.fileMenuContentList = [];
  }

  setPasteMenuClientRect(clientRect?: DOMRect) {
    this.pasteMenuClientRect = clientRect;
  }

  setPasteMenuContentList(contentList: MDSMenuContentList) {
    this.pasteMenuContentList = contentList;
    // Auto-select first item when menu opens, reset when menu closes
    this.setPasteMenuKeyboardSelectedIndex(contentList.length > 0 ? 0 : -1);
  }

  setPasteMenuKeyboardSelectedIndex(pasteMenuKeyboardSelectionIndex: number) {
    this.pasteMenuKeyboardSelectionIndex = pasteMenuKeyboardSelectionIndex;
  }

  setNote(noteObservable?: INoteObservable | TemplateObservable) {
    if (this.noteObservable !== noteObservable) {
      this.noteObservable = noteObservable;
    }
  }

  setReloadRequired = (reload = true) => {
    logger.debug({ message: `[${this.id}] MemCommonEditorStore: reload=${reload}` });
    if (!reload) {
      this.reloadRequired = false;
      return;
    }
    this.debouncedReloadRequired();
  };

  private debouncedReloadRequired = debounce(
    async () => {
      // To disable fast-reload:
      // runInAction(() => {
      //   this.reloadRequired = true;
      // });
      // return;

      runInAction(() => {
        // Reset state (other than diff mode).
        this.reloadRequired = false;
        this.isShowingDifferences = false;
        this.isNoteEmpty = false;
        this.edited = false;
        this.mentionQuery = "";
        this.mentionClientRect = undefined;
        this.mentionAvailableChips = undefined;
        this.scrollIntoViewMentionKeyboardSelectionChipIndex = false;
        this.mentionKeyboardSelectionChipIndex = 1;
        this.mouseY = 0;
        this.lockedMouseY = 0;
        this.tableMenuClientRect = undefined;
        this.tableMenuPlacement = "top-start";
        this.tableMenuOffsetOptions = undefined;
        this.tableMenuContentList = [];
        this.fileMenuClientRect = undefined;
        this.fileMenuContentList = [];
        this.pasteMenuClientRect = undefined;
        this.pasteMenuContentList = [];
        this.getAvailableChipsCalls = 0;
        this.mentionTooltipParams = {
          top: 0,
          left: 0,
          visible: false,
          itemId: "",
        };
      });

      // Soft-reload that re-initializes the editor.
      const config = await this.editorInitializer();

      // Implies FlushUpdates, (smart-)Reset.
      this.dispatchAction({
        kind: MemCommonEditorActionKind.Initialize,
        payload: {
          ...config,
        },
      });
    },
    500,
    { leading: false, trailing: true }
  );

  private clearSuggestionPreviewAndReload({ reason }: { reason: string }) {
    logger.debug({
      message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Clearing suggestion preview and reloading: ${reason}`,
    });

    runInAction(() => {
      // Only reset autoFocusPosition if it was explicitly set for differences.
      // Don't override user-intended position (e.g. "end" for new notes).
      if (!this.autoFocusPosition) {
        this.autoFocusPosition = "start";
      }
      this.isShowingDifferences = false;
      this.showDifferencesAction = undefined;
      this.versionToAccept = undefined;
      this.baseVersion = undefined;
      // Do a non-fast reload to reset the store.
      this.reloadRequired = true;
    });
  }

  private get isShowingSuggestionDifferences() {
    return (
      this.showDifferencesAction?.kind === MemCommonEditorActionKind.ShowDifferencesTo &&
      this.showDifferencesAction.payload.mode === "accept-voice-transcript-changes"
    );
  }

  setHasContentError = () => {
    this.hasContentError = true;
  };

  setHasFatalError = () => {
    this.hasFatalError = true;
  };

  handleDropdownHover = ({ itemId }: { itemId?: string }) => {
    // Lock hover to select until mouse is moved enough.
    // Since it's assigned to mouseenter/leave, this only takes effect when a new element is hovered.
    if (this.lockedMouseY) return;

    this.mentionKeyboardSelectionChipIndex =
      this.mentionAvailableChips?.findIndex(e => e.id === itemId) ?? -1;
    this.scrollIntoViewMentionKeyboardSelectionChipIndex = false;
  };

  private acceptCleanupAfterPaste = () => {
    this.closePasteMenu({ unblockInputImmediately: false });

    if (!(this.noteObservable instanceof NoteObservable)) {
      return;
    }

    const note = this.noteObservable as NoteObservable;
    note.cleanup();
  };

  private closePasteMenu = ({
    unblockInputImmediately = true,
  }: { unblockInputImmediately?: boolean } = {}) => {
    if (this.pasteMenuContentList.length === 0) return;
    this.setPasteMenuClientRect(undefined);
    this.setPasteMenuContentList([]);

    const unlock = () => {
      this.dispatchAction({
        kind: MemCommonEditorActionKind.SetInputBlocked,
        payload: {
          blocked: false,
        },
      });
    };

    if (unblockInputImmediately) {
      unlock();
      return;
    }

    requestAnimationFrame(() => {
      unlock();
    });
  };

  handleSelectPasteMenuItem = () => {
    if (this.pasteMenuContentList.length === 0) return;

    const selectedIndex = Math.max(this.pasteMenuKeyboardSelectionIndex, 0);
    const selectedItem = this.pasteMenuContentList[selectedIndex];

    if (!selectedItem || selectedItem.kind !== MDSMenuContentItemKind.Item) return;

    // Execute the onClick handler
    selectedItem.onClick?.();
  };

  handleSelectMention = async (option?: MemCommonEditorMentionChip) => {
    if (!this.mentionAvailableChips) return;

    const mention = (() => {
      if (option) return option;

      return this.mentionAvailableChips[Math.max(this.mentionKeyboardSelectionChipIndex, 0)];
    })();

    if (!mention) return;

    const action = typeof mention.action === "function" ? await mention.action() : mention.action;
    if (!action && typeof mention.action === "function") return;

    try {
      await mention.beforeSelection?.();
    } catch (_err) {
      // If creation was denied (e.g., feature gate), do not close/insert
      return;
    }

    if (this.mentionQuery.startsWith("#")) {
      this.noteObservable?.collectionList?.addCollection({
        collectionId: mention.id,
        triggerSuccessToast: false,
        eventContext: EventContext.EditorInline,
        wait: false,
      });
    }

    this.dispatchAction({
      kind: MemCommonEditorActionKind.CloseMention,
      payload: {
        id: mention.id,
        kind: this.mentionQuery.startsWith("#")
          ? MemCommonMentionKind.Collection
          : // if it starts w/ "/" it's safe to return Note here because no mention is inserted.
            MemCommonMentionKind.Note,
        label: mention.menuItem.label,
        action,
      },
    });
  };

  resetMentionTooltipParams = () => {
    this.mentionTooltipParams = {
      top: 0,
      left: 0,
      visible: false,
      itemId: "",
    };
  };

  setOnGhostTextAccepted = (callback: (() => void) | undefined) => {
    this.onGhostTextAccepted = callback;
  };

  showGhostText = ({
    encodedContent,
    onAccepted,
  }: {
    encodedContent: string;
    onAccepted?: () => void;
  }) => {
    this.setOnGhostTextAccepted(onAccepted);
    this.dispatchAction({
      kind: MemCommonEditorActionKind.ShowGhostText,
      payload: {
        docContent: { encodedContent },
        hintContent: {
          tiptapJson: {
            type: "doc",
            content: [
              { type: "paragraph", content: [] },
              {
                type: "paragraph",
                content: [
                  { type: "ghostHintIcon", attrs: { kind: "tab-key" } },
                  { type: "text", text: " to accept" },
                ],
              },
            ],
          },
        },
        config: { ensureTrailingNewline: true },
      },
    });
  };

  clearGhostText = () => {
    this.setOnGhostTextAccepted(undefined);
    this.dispatchAction({
      kind: MemCommonEditorActionKind.RejectGhostText,
      payload: null,
    });
  };

  private setMentionTooltipParams = (target: EventTarget | null, itemId: string) => {
    if (!target || !(target instanceof HTMLElement)) {
      return this.resetMentionTooltipParams();
    }

    const element = target as HTMLElement;
    const rect = element.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

    this.mentionTooltipParams = {
      top: rect.top + scrollTop - 6, // Position above with small offset
      left: rect.left + rect.width / 2 + scrollLeft, // Center horizontally
      visible: true,
      itemId,
    };
  };

  processSuggestionData({
    suggestionData,
    isUpdate = true,
  }: {
    suggestionData: NonNullable<NoteSuggestionData>;
    isUpdate?: boolean;
  }) {
    const { suggested_encoded_content_diff, suggested_encoded_content, prev_encoded_content } =
      suggestionData;

    const baseVersion: NoteContentVersion = {
      model_id: "",
      model_kind: "NOTE_CONTENT_DOCUMENT",
      model_version: 0,
      model_data: {
        note_id: this.id,
        encoded_content: prev_encoded_content,
      },
      model_scopes: [],
    };
    const finalVersion: NoteContentVersion = {
      model_id: "",
      model_kind: "NOTE_CONTENT_DOCUMENT",
      model_version: 0,
      model_data: {
        note_id: this.id,
        encoded_content: suggested_encoded_content_diff ?? suggested_encoded_content,
      },
      model_scopes: [],
    };
    const patchId = `suggestion-${suggestionData.id}`;
    const contentIsNotStrictlyAhead = !suggested_encoded_content_diff;
    this.showDifferencesToAcceptVoiceTranscriptChanges({
      patchId,
      finalVersion,
      baseVersion,
      contentIsNotStrictlyAhead,
      isUpdate,
    });
  }

  showDifferencesToAcceptVoiceTranscriptChanges = ({
    patchId,
    finalVersion,
    baseVersion,
    contentIsNotStrictlyAhead = false,
    isUpdate = true,
  }: {
    patchId: string;
    finalVersion: NoteContentVersion;
    baseVersion?: NoteContentVersion;
    contentIsNotStrictlyAhead?: boolean;
    isUpdate?: boolean;
  }) => {
    this.showDifferencesBetweenVersions({
      finalVersion,
      baseVersion,
      patchId,
      mode: "accept-voice-transcript-changes",
      contentIsNotStrictlyAhead,
      isUpdate,
    });
  };

  showDifferencesBetweenVersions = ({
    finalVersion,
    baseVersion,
    patchId,
    mode = "show-differences",
    contentIsNotStrictlyAhead = true,
    isUpdate = true,
  }: {
    finalVersion: NoteContentVersion;
    baseVersion?: NoteContentVersion;
    patchId?: string;
    mode?: PatchContentMode;
    contentIsNotStrictlyAhead?: boolean;
    isUpdate?: boolean;
  }) => {
    const action: MemCommonEditorAction = {
      kind: MemCommonEditorActionKind.ShowDifferencesTo,
      payload: {
        patchId: patchId ?? `v${finalVersion.model_version}`,
        mode,
        baseEncodedContent: baseVersion?.model_data.encoded_content ?? undefined,
        encodedContentUpdate: finalVersion.model_data.encoded_content!,
        contentIsNotStrictlyAhead,
      },
    };

    if (this.showDifferencesAction && isEqual(this.showDifferencesAction, action)) return;

    this.autoFocusPosition = "start";
    this.showDifferencesAction = action;
    this.versionToAccept = finalVersion;
    this.baseVersion = baseVersion;

    if (isUpdate) {
      this.debouncedReloadRequired();
    } else {
      runInAction(() => {
        this.isShowingDifferences = true;
      });
    }
  };

  closeDifferences = () => {
    const reloadRequired =
      this.isShowingDifferences ||
      !!this.showDifferencesAction ||
      !!this.versionToAccept ||
      !!this.baseVersion;
    this.isShowingDifferences = false;
    this.autoFocusPosition = "start";
    this.showDifferencesAction = undefined;
    this.versionToAccept = undefined;
    this.baseVersion = undefined;
    if (reloadRequired) this.debouncedReloadRequired();
  };

  closeSuggestion = async (accept: boolean) => {
    if (!this.noteObservable || this.noteObservable instanceof TemplateObservable) return;

    const noteObservable = this.noteObservable as NoteObservable;
    if (accept) {
      await noteObservable.acceptSuggestion();
    } else {
      await noteObservable.rejectSuggestion();
    }

    this.closeDifferences();
  };

  /**
   * Prepares a PasteContent action from pending clipboard content in the store.
   * This is used when a note is created via the global paste feature.
   * Reads clipboard data from store, prepares the action, and cleans up the store.
   * @returns MemCommonEditorAction for pasting content, or undefined if no clipboard content available
   */
  private preparePasteClipboardContentAction = (): MemCommonEditorAction | undefined => {
    // Check if appStore is available (it provides access to notes store)
    if (!this.store) {
      return;
    }

    const clipboardContentFromStore = this.store.notes.pendingClipboardContent;

    // Only process clipboard content if it exists and is intended for this specific note
    // This prevents paste from going to the wrong editor when multiple panes are open
    if (!clipboardContentFromStore || clipboardContentFromStore.noteId !== this.id) {
      return;
    }

    if (this.readOnly || this.noteObservable?.isTrashed || !this.noteObservable?.canWrite) {
      return;
    }

    logger.debug({
      message: `[${this.id}] MemCommonEditorStore: Preparing paste clipboard content action`,
      info: {
        noteId: this.id,
        hasText: !!clipboardContentFromStore.text,
        hasHtml: !!clipboardContentFromStore.html,
      },
    });

    // Prepare the PasteContent action with text and html strings
    const action: MemCommonEditorAction = {
      kind: MemCommonEditorActionKind.PasteContent,
      payload: {
        text: clipboardContentFromStore.text,
        html: clipboardContentFromStore.html,
        context: "notes_actions",
      },
    };

    // Clear clipboard content from store after extracting data
    // This prevents the paste from firing again and is a one-time consumption
    this.store.notes.clearPendingClipboardContent();

    return action;
  };

  // COMPUTED

  get editorInitializer(): MemCommonEditorInitializer {
    return async () => {
      const { encodedContent, queuedUpdates, processedUpdateIds } = (() => {
        if (this.showDifferencesAction) {
          return {
            encodedContent: undefined,
            queuedUpdates: [],
            processedUpdateIds: new Set<string>(),
          };
        }

        if (this.queuedUpdates[0]?.isDestructive) {
          return {
            encodedContent: undefined,
            queuedUpdates: this.queuedUpdates,
            processedUpdateIds: new Set<string>(),
          };
        }

        return {
          encodedContent: this.noteObservable?.remoteContent ?? undefined,
          queuedUpdates: this.queuedUpdates,
          processedUpdateIds: new Set(this.noteObservable?.queuedDocumentUpdates.map(e => e.id)),
        };
      })();

      this.processedUpdateIds = processedUpdateIds;

      const encodedDocumentUpdates = queuedUpdates
        .map(e => e.encodedContentDiff)
        .filter(s => s.length > 0);

      logger.debug({
        message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Initialize ${this.readOnly ? "read-only" : "writable"} note editor with ${encodedContent?.length} bytes, updates with ${encodedDocumentUpdates?.map(s => s.length).join(", ")} bytes`,
        info: { processedUpdateIds: Array.from(this.processedUpdateIds) },
      });

      const defaultEditorContext = editorContextModule.generateDefaultEditorContext();

      const context: MemCommonEditorContext =
        (await this.noteObservable?.generateCommonEditorContext()) ?? defaultEditorContext;

      const suggestionData = this.noteObservable?.suggestionData;
      if (suggestionData && !this.showDifferencesAction) {
        this.processSuggestionData({ suggestionData, isUpdate: false });
      } else if (!this.isShowingDifferences) {
        // Leave diff mode immediately but only enter in
        // MemCommonEditorEventKind.IsShowingDifferences to reduce re-renders.
        const isShowingDifferences = !!this.showDifferencesAction;
        if (isShowingDifferences !== this.isShowingDifferences) {
          runInAction(() => {
            this.isShowingDifferences = isShowingDifferences;
          });
        }
      }

      // Prepare actions to dispatch on editor initialization
      const actions: MemCommonEditorAction[] = [];
      if (this.showDifferencesAction) {
        actions.push(this.showDifferencesAction);
      }
      const pasteClipboardContentAction = this.preparePasteClipboardContentAction();
      if (pasteClipboardContentAction) {
        actions.push(pasteClipboardContentAction);
      }

      // Dispatch initial CU state from context so badges show on first render.
      const cuFiles: Record<string, MemCommonEditorContentUnderstandingInfo | undefined> = {};
      for (const [fileId, fileData] of Object.entries(context.files) as [
        string,
        { contentUnderstanding?: MemCommonEditorContentUnderstandingInfo },
      ][]) {
        if (fileData.contentUnderstanding) {
          cuFiles[fileId] = fileData.contentUnderstanding;
        }
      }
      if (Object.keys(cuFiles).length > 0) {
        actions.push({
          kind: MemCommonEditorActionKind.UpdateFileContentUnderstanding,
          payload: { files: cuFiles },
        });
      }

      const autoFocus =
        !this.isShowingDifferences && (this.autoFocus ?? this.publicStore?.interface.isHoverable);

      return {
        encodedContent,
        encodedDocumentUpdates,
        toolbar: !this.readOnly,
        readOnly:
          this.readOnly || !!this.noteObservable?.isTrashed || !this.noteObservable?.canWrite,
        autoFocus,
        position: this.autoFocusPosition,
        disableAutoScroll: this.disableGlobalListeners,
        enableImageZoomCursor: this.enableImageZoomCursor,
        enableImageHoverToolbar: this.enableImageHoverToolbar,
        canShowEmbeddedPDFs: true,
        disableGlobalListeners: this.disableGlobalListeners,
        noteId: this.id,
        enableWebSockets: false,
        maxUpdateThrottleMillis: 2000,
        placeholder: this.placeholder,
        htmlPlaceholderPreset: this.htmlPlaceholderPreset,
        onEditor: (editor?: Editor | null) => {
          runInAction(() => (this.editor = editor ?? undefined));

          // Manually set cursor position if autoFocusPosition is set and editor is ready
          if (editor && this.autoFocusPosition && autoFocus) {
            // Use setTimeout to ensure content is fully loaded before setting cursor position
            if (this.autoFocusPosition === "end") {
              const endPos = editor.state.doc.content.size;
              editor.chain().focus().setTextSelection(endPos).run();
            } else if (this.autoFocusPosition === "start") {
              editor.chain().focus().setTextSelection(0).run();
            }
          }
        },
        metadata: undefined,
        theme: this.uiTheme,
        webSocketAuthToken: undefined,
        context,
        actions,
        validateFileBeforeInsert: this.buildValidateFileBeforeInsert(),
      };
    };
  }

  get editorEventHandler() {
    return (event: MemCommonEditorEvent) => {
      logger.debug({
        message: "[MemCommonEditorStore] Event received",
        info: { event: objectModule.safeAsJson(event) },
      });

      switch (event.kind) {
        case MemCommonEditorEventKind.EditorOperationLog: {
          const { operation, severity, details } = event.payload;
          const info = { noteId: this.id, ...(details ?? {}) };
          const logEntry = {
            message: `[MemCommonEditorStore][EditorOperationLog][${this.id}] ${operation}`,
            info,
          };

          if (operation === MemCommonEditorOperationLogOperation.PasteMarkdown) {
            trackEvent(TrackedEvent.NotePasteMarkdown);
          }

          // When user retries a failed image, refresh the asset token and reload the page
          // to ensure fresh credentials are used for image proxying
          if (operation === MemCommonEditorOperationLogOperation.ImageFetchRetried) {
            logger.info({
              message: `[MemCommonEditorStore] Image retry requested, refreshing asset token and reloading page`,
              info,
            });
            void this.store?.auth.assetAccessTokenState.regenerate().then(() => {
              void windowModule.forceReload();
            });
            break;
          }

          switch (severity) {
            case MemCommonEditorOperationLogSeverity.Error: {
              logger.error(logEntry);
              break;
            }
            case MemCommonEditorOperationLogSeverity.Warn: {
              logger.warn(logEntry);
              break;
            }
            case MemCommonEditorOperationLogSeverity.Info:
            default: {
              logger.info(logEntry);
              break;
            }
          }
          break;
        }
        case MemCommonEditorEventKind.ReadOnlyNoteClicked: {
          this.onReadOnlyNoteClicked?.();
          break;
        }
        case MemCommonEditorEventKind.FocusChanged: {
          if (!event.payload.focus) {
            const noteObservable = this.noteObservable;
            if (noteObservable instanceof NoteObservable) {
              const noteId = noteObservable.id;
              const primaryLabel = noteObservable.primaryLabelText ?? "";
              noteObservable.store.headsUp.handleEditorBlur({
                noteId,
                primaryLabel,
                isEmpty: this.isNoteEmpty,
              });
            }
          }
          break;
        }
        case MemCommonEditorEventKind.ItemOver: {
          switch (event.payload.kind) {
            case MemCommonEditorClickedItemKind.Mention: {
              this.setMentionTooltipParams(event.payload.eventTarget, event.payload.id);
              break;
            }
          }
          break;
        }
        case MemCommonEditorEventKind.ItemOut: {
          switch (event.payload.kind) {
            case MemCommonEditorClickedItemKind.Mention: {
              this.setMentionTooltipParams(null, event.payload.id);
              break;
            }
          }
          break;
        }
        case MemCommonEditorEventKind.ItemClicked: {
          switch (event.payload.kind) {
            case MemCommonEditorClickedItemKind.Task:
              // Nothing special on web.
              break;
            case MemCommonEditorClickedItemKind.Hyperlink:
              // Link is opened from toolbar.
              break;
            case MemCommonEditorClickedItemKind.Image: {
              if (event.payload.actionSource === "hover-toolbar") {
                trackEvent(TrackedEvent.EditorImageHoverToolbarOpen, {
                  note_id: this.id,
                  image_id: event.payload.imageId ?? null,
                });
              }
              const images = mapPayloadImagesToImageLightboxItems(event.payload.images);
              this.openImageViewerForSrc(event.payload.src ?? null, images);
              break;
            }
            case MemCommonEditorClickedItemKind.Mention: {
              const { url, mentionKind, id, keys } = event.payload;
              if (this.goToMention) {
                this.goToMention({ url, mentionKind, id, keys });
              } else {
                windowModule.openInNewTab({ url: new URL(url) });
              }
              break;
            }
          }
          break;
        }

        case MemCommonEditorEventKind.TableMenu: {
          this.setTableMenuClientRect(event.payload.clientRect ?? undefined);
          switch (event.payload.kind) {
            case MemCommonTableMenuKind.None: {
              this.setTableMenuPlacement("bottom-start");
              this.setTableMenuOffsetOptions();
              this.setTableMenuContentList([]);
              break;
            }
            case MemCommonTableMenuKind.Column: {
              this.dispatchAction({
                kind: MemCommonEditorActionKind.BlurEditor,
                payload: null,
              });
              this.setTableMenuPlacement("top");
              this.setTableMenuOffsetOptions([16]);
              this.setTableMenuContentList([
                {
                  id: "add-column-before",
                  kind: MDSMenuContentItemKind.Item,
                  variant: MDSMenuItemVariant.OneLine,
                  label: "Add column before",
                  icon: MDSIconKind.ArrowLeftToLine,
                  onClick: () =>
                    this.dispatchAction({
                      kind: MemCommonEditorActionKind.ChangeTable,
                      payload: {
                        operation: ChangeTableOperation.Insert,
                        position: ChangeTablePosition.Before,
                        target: ChangeTableTarget.Column,
                      },
                    }),
                },
                {
                  id: "add-column-after",
                  kind: MDSMenuContentItemKind.Item,
                  variant: MDSMenuItemVariant.OneLine,
                  label: "Add column after",
                  icon: MDSIconKind.ArrowRightToLine,
                  onClick: () =>
                    this.dispatchAction({
                      kind: MemCommonEditorActionKind.ChangeTable,
                      payload: {
                        operation: ChangeTableOperation.Insert,
                        position: ChangeTablePosition.After,
                        target: ChangeTableTarget.Column,
                      },
                    }),
                },
                {
                  id: "delete-column",
                  kind: MDSMenuContentItemKind.Item,
                  variant: MDSMenuItemVariant.OneLine,
                  label: "Delete column",
                  icon: MDSIconKind.Trash,
                  onClick: () =>
                    this.dispatchAction({
                      kind: MemCommonEditorActionKind.ChangeTable,
                      payload: {
                        operation: ChangeTableOperation.Delete,
                        target: ChangeTableTarget.Column,
                      },
                    }),
                },
              ]);
              break;
            }
            case MemCommonTableMenuKind.Row: {
              this.dispatchAction({
                kind: MemCommonEditorActionKind.BlurEditor,
                payload: null,
              });
              this.setTableMenuPlacement("left");
              this.setTableMenuOffsetOptions([4]);
              this.setTableMenuContentList([
                {
                  id: "add-row-before",
                  kind: MDSMenuContentItemKind.Item,
                  variant: MDSMenuItemVariant.OneLine,
                  label: "Add row above",
                  icon: MDSIconKind.ArrowUpToLine,
                  onClick: () =>
                    this.dispatchAction({
                      kind: MemCommonEditorActionKind.ChangeTable,
                      payload: {
                        operation: ChangeTableOperation.Insert,
                        position: ChangeTablePosition.Before,
                        target: ChangeTableTarget.Row,
                      },
                    }),
                },
                {
                  id: "add-row-after",
                  kind: MDSMenuContentItemKind.Item,
                  variant: MDSMenuItemVariant.OneLine,
                  label: "Add row below",
                  icon: MDSIconKind.ArrowDownToLine,
                  onClick: () =>
                    this.dispatchAction({
                      kind: MemCommonEditorActionKind.ChangeTable,
                      payload: {
                        operation: ChangeTableOperation.Insert,
                        position: ChangeTablePosition.After,
                        target: ChangeTableTarget.Row,
                      },
                    }),
                },
                {
                  id: "delete-row",
                  kind: MDSMenuContentItemKind.Item,
                  variant: MDSMenuItemVariant.OneLine,
                  label: "Delete row",
                  icon: MDSIconKind.Trash,
                  onClick: () =>
                    this.dispatchAction({
                      kind: MemCommonEditorActionKind.ChangeTable,
                      payload: {
                        operation: ChangeTableOperation.Delete,
                        target: ChangeTableTarget.Row,
                      },
                    }),
                },
              ]);
              break;
            }
          }
          break;
        }

        case MemCommonEditorEventKind.MentionUpdateQuery: {
          const { clientRect, mentionQuery } = event.payload;
          this.setMentionClientRect(clientRect ?? undefined);
          this.setMentionQuery(mentionQuery);
          // Track call count to handle race conditions
          const currentCall = ++this.getAvailableChipsCalls;

          if (!mentionQuery || !this.mentions?.getAvailableChips) {
            // TODO: any pending getAvailableChips should be cancelled / ignored.
            this.setMentionAvailableChips(undefined);
            break;
          }

          this.mentions
            ?.getAvailableChips(mentionQuery, {
              inlineCreation: true,
              excludeId: this.id,
              noteType: this.noteType,
              isCalendarEventTemplate:
                this.noteObservable instanceof TemplateObservable &&
                this.noteObservable.isDefaultCalendarEventTemplate,
            })
            .then(availableChips => {
              if (currentCall !== this.getAvailableChipsCalls) return;
              this.setMentionAvailableChips(availableChips ?? []);
            })
            .catch(err => {
              if (currentCall !== this.getAvailableChipsCalls) return;
              this.setMentionAvailableChips(undefined);
              logger.error({
                message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Error getting available chips`,
                info: { error: objectModule.safeErrorAsJson(err) },
              });
            });

          break;
        }

        case MemCommonEditorEventKind.MentionKeyDown: {
          if (!this.mentionAvailableChips) return;

          const index = this.mentionKeyboardSelectionChipIndex;
          switch (event.payload.kind) {
            case MemCommonMentionKeyDownKind.ArrowDown: {
              this.setMentionKeyboardSelectedChipIndex(
                index < 0
                  ? 0
                  : (index + 1 + this.mentionAvailableChips.length) %
                      this.mentionAvailableChips.length
              );
              break;
            }
            case MemCommonMentionKeyDownKind.ArrowUp: {
              this.setMentionKeyboardSelectedChipIndex(
                index < 0
                  ? this.mentionAvailableChips.length - 1
                  : (index - 1 + this.mentionAvailableChips.length) %
                      this.mentionAvailableChips.length
              );
              break;
            }
            case MemCommonMentionKeyDownKind.Enter: {
              this.handleSelectMention();
              break;
            }
          }
          break;
        }

        case MemCommonEditorEventKind.FileDownloaded: {
          fileModule.downloadFileFromUrl({
            fileName: event.payload.fileName,
            downloadUrl: event.payload.downloadUrl,
          });
          break;
        }

        case MemCommonEditorEventKind.FileOpened: {
          const { fileName, downloadUrl } = event.payload;
          const isPdf = fileName.toLowerCase().endsWith(".pdf");

          if (isPdf && this.onPdfPreviewClicked && !clientEnvModule.isMobileBrowser()) {
            this.onPdfPreviewClicked(downloadUrl, fileName);
          } else {
            fileModule.downloadFileFromUrlDirectly({
              fileName,
              downloadUrl,
              inTab: true,
            });
          }
          break;
        }

        case MemCommonEditorEventKind.FileMenuOpened: {
          const { fileId, fileName, downloadUrl, clientRect, isEmbeddedPdfVisible } = event.payload;
          this.setFileMenuClientRect(
            new DOMRect(clientRect.left, clientRect.top, clientRect.width, clientRect.height)
          );

          const isPdf = fileName.toLowerCase().endsWith(".pdf");
          const items: MDSMenuContentList = [
            {
              id: "file-menu-download",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Download",
              icon: MDSIconKind.ArrowDownToLine,
              onClick: () => {
                fileModule.downloadFileFromUrl({ fileName, downloadUrl });
                this.resetFileMenu();
              },
            },
          ];

          if (fileId) {
            items.push({
              id: "file-menu-rename",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Rename",
              icon: MDSIconKind.Pen,
              onClick: () => {
                this.resetFileMenu();
                // Defer modal open so React Aria's focus restoration from
                // the file menu cleanup completes before the modal takes focus.
                requestAnimationFrame(() => {
                  this.openRenameFileModal?.({
                    fileId,
                    fileName,
                    onRename: (newFileName: string) => {
                      this.dispatchAction({
                        kind: MemCommonEditorActionKind.RenameFile,
                        payload: { fileId, newFileName },
                      });
                    },
                  });
                });
              },
            });
          }

          if (isPdf && (fileId || downloadUrl)) {
            const currentlyVisible = isEmbeddedPdfVisible ?? false;
            items.push({
              id: "file-menu-toggle-embed",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: currentlyVisible ? "Hide embed" : "Show embed",
              icon: currentlyVisible ? MDSIconKind.BrowserSlash : MDSIconKind.Browser,
              onClick: () => {
                const payload = fileId
                  ? { fileId, isVisible: !currentlyVisible }
                  : { downloadUrl, isVisible: !currentlyVisible };

                this.dispatchAction({
                  kind: MemCommonEditorActionKind.SetFileEmbedVisibility,
                  payload,
                });
                trackEvent(TrackedEvent.PdfViewerEmbedToggle, {
                  context: "editor",
                  is_visible: !currentlyVisible,
                });
                this.resetFileMenu();
              },
            });
          }
          if (isPdf && downloadUrl && this.onPdfPreviewClicked) {
            items.push({
              id: "file-menu-preview",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Open preview",
              icon: MDSIconKind.Eye,
              onClick: () => {
                this.onPdfPreviewClicked?.(downloadUrl, fileName);
                this.resetFileMenu();
              },
            });
          }

          this.setFileMenuContentList(items);
          break;
        }

        case MemCommonEditorEventKind.ImageDownloaded: {
          trackEvent(TrackedEvent.EditorImageHoverToolbarDownload, {
            note_id: this.id,
            image_id: event.payload.imageId ?? null,
          });
          void downloadImageFromUrl({
            src: event.payload.downloadUrl,
            fileName: event.payload.imageName,
          });
          break;
        }

        case MemCommonEditorEventKind.ImageOcrMenuOpened: {
          const { imageId, imageSrc, clientRect } = event.payload;
          this.setFileMenuClientRect(
            new DOMRect(clientRect.left, clientRect.top, clientRect.width, clientRect.height)
          );

          trackEvent(TrackedEvent.OcrMenuOpen, {
            image_id: imageId ?? null,
            note_id: this.id,
          });

          const ocrMenuItems: MDSMenuContentList = [
            {
              id: "ocr-menu-header",
              kind: MDSMenuContentItemKind.Header,
              label: "Insert image text into note",
            },
            {
              id: "ocr-menu-above",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Above image",
              icon: MDSIconKind.ArrowUp,
              onClick: () => {
                trackEvent(TrackedEvent.OcrInsertClick, {
                  image_id: imageId ?? null,
                  note_id: this.id,
                  position: "above",
                });
                this.dispatchAction({
                  kind: MemCommonEditorActionKind.InsertOcrMarkdown,
                  payload: { imageId, imageSrc, position: "above" },
                });
                this.resetFileMenu();
              },
            },
            {
              id: "ocr-menu-below",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Below image",
              icon: MDSIconKind.ArrowDownToLine,
              onClick: () => {
                trackEvent(TrackedEvent.OcrInsertClick, {
                  image_id: imageId ?? null,
                  note_id: this.id,
                  position: "below",
                });
                this.dispatchAction({
                  kind: MemCommonEditorActionKind.InsertOcrMarkdown,
                  payload: { imageId, imageSrc, position: "below" },
                });
                this.resetFileMenu();
              },
            },
          ];

          this.setFileMenuContentList(ocrMenuItems);
          break;
        }

        case MemCommonEditorEventKind.ImageAdded: {
          this.noteObservable?.uploadImageAssociatedWithNote({
            info: event.payload.info,
          });
          break;
        }

        case MemCommonEditorEventKind.ImageRejected: {
          switch (event.payload.errorCode) {
            case MemCommonEditorImageRejectionErrorCode.ImageTooLarge: {
              this.openImageUploadRejectedModal?.({
                imageName: event.payload.imageName,
                code: event.payload.errorCode,
              });

              break;
            }

            default: {
              toastModule.triggerToast({
                content: ATTACHMENT_INACCESSIBLE_TOAST_MESSAGE,
              });
              break;
            }
          }

          break;
        }

        case MemCommonEditorEventKind.FileAdded: {
          this.noteObservable?.uploadFileAssociatedWithNote({
            info: event.payload.info,
          });
          break;
        }

        case MemCommonEditorEventKind.FileRejected: {
          const isPdfByName = event.payload.fileName.toLowerCase().endsWith(".pdf");

          switch (event.payload.errorCode) {
            case MemCommonEditorFileRejectionErrorCode.FileTooLarge: {
              this.openFileUploadRejectedModal?.({
                fileName: event.payload.fileName,
                code: event.payload.errorCode,
              });

              if (isPdfByName) {
                trackEvent(TrackedEvent.PdfUploadValidationFailed, {
                  context: "editor",
                  reason: "too_large",
                });
              }

              break;
            }

            case MemCommonEditorFileRejectionErrorCode.FileTooManyPages: {
              if (!this.store) return;

              const gate = this.store.featureGates.checkGate(
                GatedFeature.ContentUnderstandingPdfPage
              );
              this.store.featureGates.openUsageLimitUpsell({
                used: gate.used,
                limit: gate.limit,
                feature: GatedFeature.ContentUnderstandingPdfPage,
                context: PaywallContext.ContentUnderstandingPdfPage,
              });

              trackEvent(TrackedEvent.PdfUploadValidationFailed, {
                context: "editor",
                reason: "page_limit",
                limit_bytes: undefined,
              });

              break;
            }

            case MemCommonEditorFileRejectionErrorCode.UserCancelled: {
              // User intentionally cancelled the page-limit warning — no feedback needed.
              break;
            }

            case MemCommonEditorFileRejectionErrorCode.FileTypeNotSupported: {
              toastModule.triggerToast({
                content: ATTACHMENT_INACCESSIBLE_TOAST_MESSAGE,
              });

              if (isPdfByName) {
                trackEvent(TrackedEvent.PdfUploadValidationFailed, {
                  context: "editor",
                  reason: "invalid_mime",
                });
              }

              break;
            }

            default: {
              toastModule.triggerToast({
                content: ATTACHMENT_INACCESSIBLE_TOAST_MESSAGE,
              });
              break;
            }
          }

          break;
        }

        case MemCommonEditorEventKind.IsShowingDifferences: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Note is${event.payload.isShowingDifferences ? "" : " not"} showing differences`,
          });
          runInAction(() => {
            this.isShowingDifferences = event.payload.isShowingDifferences;
          });
          break;
        }
        case MemCommonEditorEventKind.IsEmpty: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Note is${event.payload.isEmpty ? "" : " not"} empty`,
          });
          runInAction(() => {
            this.isNoteEmpty = event.payload.isEmpty;
          });
          break;
        }
        case MemCommonEditorEventKind.HasBeenEdited: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Note has been edited`,
          });
          runInAction(() => {
            this.edited = event.payload.edited;
          });
          break;
        }
        case MemCommonEditorEventKind.DocumentMetadataChanged: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) ${this.readOnly ? "Read-only" : "Writable"} note title changed`,
          });
          if (this.readOnly) break;

          // Reusing implementation, empty diff is ignored later.

          this.noteObservable?.updateContentUsingDiff({
            encodedContentDiff: null,
            primaryLabel: event.payload.primaryLabel ?? "",
            secondaryLabel: event.payload.secondaryLabel ?? "",
            mediaKinds: undefined,
            mentions: undefined,
            transactionMetadata: event.payload.transactionMetadata,
          });
          break;
        }
        case MemCommonEditorEventKind.DocumentChanged: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) ${this.readOnly ? "Read-only" : "Writable"} note changed, ${event.payload.encodedContentDiff.length} bytes`,
          });
          if (this.readOnly) break;

          const { noteObservable } = this;
          if (!noteObservable) {
            logger.error({ message: `[${this.id}] MemCommonEditorStore: note instance is gone` });
            return;
          }

          noteObservable.updateContentUsingDiff({
            encodedContentDiff: event.payload.encodedContentDiff,
            primaryLabel: event.payload.primaryLabel ?? "",
            secondaryLabel: event.payload.secondaryLabel ?? "",
            mediaKinds: event.payload.mediaKinds,
            transactionMetadata: event.payload.transactionMetadata,
            mentions: event.payload.mentions,
            templateId: event.payload.templateId,
            isEmpty: event.payload.isEmpty ?? false,
          });
          if (event.payload.patchId) {
            logger.error({
              message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Patch ${event.payload.patchId}? Reloading...`,
            });
            this.debouncedReloadRequired();
          }
          break;
        }
        case MemCommonEditorEventKind.SearchResults: {
          this.findInNote?.setSearchResults(event.payload);
          break;
        }
        case MemCommonEditorEventKind.TemplateInserted: {
          const { templateId } = event.payload;
          logger.warn({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Template insertion should be part of a content diff`,
            info: { templateId },
          });

          if (this.noteType !== NoteType.Note) break;
          this.store?.headsUp.handleTemplateInserted({
            noteId: this.id,
            primaryLabel: this.noteObservable?.title || "",
            isEmpty: this.isNoteEmpty,
            templateId,
          });
          break;
        }
        case MemCommonEditorEventKind.EditorContentRendered: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: content rendered`,
          });
          runInAction(() => {
            this.editorContentRendered = true;
          });
          break;
        }
        case MemCommonEditorEventKind.ContentError: {
          logger.error({
            message: `[${this.id}] MemCommonEditorStore: content error ${event.payload.message}`,
          });
          this.setHasContentError();
          break;
        }
        case MemCommonEditorEventKind.FatalError: {
          logger.error({
            message: `[${this.id}] MemCommonEditorStore: fatal error ${event.payload.code}`,
          });
          this.setHasFatalError();
          break;
        }
        case MemCommonEditorEventKind.ContentPaste: {
          trackEvent(TrackedEvent.ClipboardPaste, {
            context: event.payload.context,
            contentsHTML: event.payload.contentsHTML,
            isShiftKeyPressed: event.payload.isShiftKeyPressed,
            contentsPlainText: event.payload.contentsPlainText,
          });

          break;
        }

        case MemCommonEditorEventKind.ContentPasteAfter: {
          if (!event.payload.clientRect) {
            return;
          }

          if (!(this.noteObservable instanceof NoteObservable)) {
            return;
          }

          // Skip if pasted content is from Mem (has Mem generator meta tag)
          const contentsHTML = event.payload.contentsHTML || "";
          if (hasMetaGeneratorTag(contentsHTML, "Mem")) {
            return;
          }

          // Show paste action menu if we have a clientRect
          this.dispatchAction({
            kind: MemCommonEditorActionKind.SetInputBlocked,
            payload: {
              blocked: true,
            },
          });

          this.setPasteMenuClientRect(event.payload.clientRect ?? undefined);
          // Note: setPasteMenuContentList will auto-select the first item
          this.setPasteMenuContentList([
            {
              id: "keep-as-is",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Keep as is",
              icon: MDSIconKind.Check,
              selectionVariant: MDSMenuItemSelectionVariant.Hover,
              rightAddon: () => <MDSShortcutKey character={MDSShortcutKeyCharacter.Escape} />,
              onClick: () => {
                this.closePasteMenu({ unblockInputImmediately: false });
              },
            },
            {
              id: "cleanup-paste",
              kind: MDSMenuContentItemKind.Item,
              variant: MDSMenuItemVariant.OneLine,
              label: "Clean up",
              icon: MDSIconKind.Cleanup,
              selectionVariant: MDSMenuItemSelectionVariant.Hover,
              rightAddon: () => <MDSShortcutKey character={MDSShortcutKeyCharacter.Tab} />,
              onClick: () => {
                this.acceptCleanupAfterPaste();
              },
            },
          ]);

          break;
        }

        case MemCommonEditorEventKind.KeyDown: {
          // Handle paste menu keyboard navigation
          const { key } = event.payload;
          this.handlePasteKeydown(key);
          break;
        }

        case MemCommonEditorEventKind.SelectionChanged: {
          // Close paste menu when user changes selection (typing, clicking, etc.)
          this.closePasteMenu({ unblockInputImmediately: false });
          break;
        }

        case MemCommonEditorEventKind.GhostTextAccepted: {
          logger.debug({
            message: `[${this.id}] MemCommonEditorStore: (${new Date().toISOString()}) Ghost text accepted`,
          });
          if (this.onGhostTextAccepted) {
            this.onGhostTextAccepted();
            this.setOnGhostTextAccepted(undefined);
          }
          break;
        }
      }
    };
  }

  private handlePasteKeydown(key: string) {
    if (this.pasteMenuContentList.length === 0) {
      return;
    }

    switch (key) {
      case "ArrowDown": {
        // Navigate to next item
        const nextIndex =
          (this.pasteMenuKeyboardSelectionIndex + 1) % this.pasteMenuContentList.length;
        this.setPasteMenuKeyboardSelectedIndex(nextIndex);
        break;
      }
      case "ArrowUp": {
        // Navigate to previous item
        const prevIndex =
          (this.pasteMenuKeyboardSelectionIndex - 1 + this.pasteMenuContentList.length) %
          this.pasteMenuContentList.length;
        this.setPasteMenuKeyboardSelectedIndex(prevIndex);
        break;
      }
      case "Enter": {
        // Execute selected item
        this.handleSelectPasteMenuItem();
        break;
      }
      case "Escape": {
        this.closePasteMenu({ unblockInputImmediately: false });
        break;
      }
      case "Tab": {
        this.acceptCleanupAfterPaste();
        break;
      }
      default: {
        // For any other key, unblock input and close menu
        this.closePasteMenu();
        break;
      }
    }
  }

  get mentionContentList(): MDSMenuContentList {
    const output: MDSMenuContentItem[] = [];
    if (!this.mentionAvailableChips) return output;

    let lastGroupTitle = "";
    for (let i = 0; i < this.mentionAvailableChips.length; i++) {
      const chip = this.mentionAvailableChips[i];
      const groupTitle = chip.groupTitle;

      if (groupTitle && groupTitle !== lastGroupTitle) {
        output.push({
          id: `group-${groupTitle}`,
          kind: MDSMenuContentItemKind.Header,
          label: groupTitle,
        });
        lastGroupTitle = groupTitle;
      }

      output.push({
        ...chip.menuItem,
        onClick: async () => this.handleSelectMention(chip),
        onHover: () => this.handleDropdownHover?.({ itemId: chip.id }),
        kind: MDSMenuContentItemKind.Item,
        selectionVariant: MDSMenuItemSelectionVariant.Hover,
        isSelected: i === this.mentionKeyboardSelectionChipIndex,
      });
    }

    return output;
  }

  get pasteMenuContentListWithSelection(): MDSMenuContentList {
    return this.pasteMenuContentList.map((item, index) => {
      if (item.kind !== MDSMenuContentItemKind.Item) return item;

      return {
        ...item,
        isSelected: index === this.pasteMenuKeyboardSelectionIndex,
      };
    });
  }

  get isInsertMenuActive() {
    return this.mentionQuery.startsWith("/");
  }

  get hasSources() {
    if (!(this.noteObservable instanceof NoteObservable)) return false;
    return this.noteObservable.hasSources;
  }

  get authorNames() {
    if (!this.noteObservable) return;

    const contacts = Array.from(this.noteObservable.authors);

    if (this.edited && this.myAccount && !contacts.some(e => e.id === this.myAccount?.id)) {
      contacts.push(this.myAccount);
    }

    return getContactDisplayNames({ contacts });
  }

  get metadata() {
    if (!this.noteObservable?.canAccess) return "";

    return getNoteMetadata({ note: this.noteObservable, authorNames: this.authorNames });
  }

  get metadataTooltip() {
    if (!this.noteObservable || this.noteObservable instanceof TemplateObservable) return "";

    const modifiedDateTime = DateTime.fromISO(this.noteObservable.modifiedAt);

    const lastEditDate = modifiedDateTime.toFormat("MMM d, yyyy");
    const lastEditTime = modifiedDateTime.toFormat("h:mm a");

    const { authorNames } = this;
    return `Last edited ${lastEditDate} at ${lastEditTime} ${
      authorNames ? ` by ${authorNames}` : ""
    }`;
  }

  get isTableMenuOpen() {
    return this.tableMenuContentList.length >= 1;
  }

  private buildValidateFileBeforeInsert(): (file: File) => Promise<ValidateFileBeforeInsertResult> {
    return async (file: File): Promise<ValidateFileBeforeInsertResult> => {
      const { store } = this;
      if (!store) return { isValid: true };

      const mimeType = commonEditorFileModule.parseMimeType({ fileName: file.name });
      if (mimeType !== "application/pdf") return { isValid: true };

      const isPdfPaywallEnabled =
        store.spaceAccountFeatureFlags.getFlagStatus("pdf-paywall") ?? false;
      if (!isPdfPaywallEnabled) return { isValid: true };

      const gate = store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage);

      let pdfPageCount = 0;
      try {
        pdfPageCount = await countPdfPages(file);
      } catch (error) {
        logger.warn({
          message: "[MemCommonEditorStore] Failed to count PDF pages, allowing upload",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
      const wouldExceedQuota = gate.used + pdfPageCount > gate.limit;

      if (wouldExceedQuota) {
        return {
          isValid: false,
          errorCode: MemCommonEditorFileRejectionErrorCode.FileTooManyPages,
        };
      }

      const PAGE_LIMIT = 100;
      if (pdfPageCount > PAGE_LIMIT) {
        const confirmed = await this.openPdfPageLimitWarningModal({
          pageCount: pdfPageCount,
        });
        if (!confirmed) {
          return {
            isValid: false,
            errorCode: MemCommonEditorFileRejectionErrorCode.UserCancelled,
          };
        }
      }

      return { isValid: true };
    };
  }
}
